import type { CSSProperties } from 'react';

// Per-finding classification summary, present iff a council run was a
// verifier-mode pass.
export interface VerifierResultsCounts {
  resolved: number;
  unresolved: number;
  partial: number;
  withdrawn: number;
  // The fifth status (engineering-plugins#291): the finding was wrong when it
  // was raised. 0 on every run from an orchestrator predating it, which is why
  // the icon below is hidden at 0 rather than shown — an unbroken row of `0 ✗`
  // across historical runs would read as a measurement, not as absence.
  refuted: number;
}

// Defensive coercion: a partial verifier_results object (e.g., a runner
// regression that drops one of the count keys) still renders the chip,
// with missing keys clamped to 0. Non-object input drops the chip entirely.
export function parseVerifierResults(raw: unknown): VerifierResultsCounts | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const num = (k: string): number => {
    const v = o[k];
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0;
  };
  return {
    resolved: num('resolved'),
    unresolved: num('unresolved'),
    partial: num('partial'),
    withdrawn: num('withdrawn'),
    refuted: num('refuted'),
  };
}

// Shared small-pill style. Visual weight is deliberately low — these are
// sub-context next to a primary label, not standalone status.
const BADGE_STYLE_BASE: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 999,
  fontSize: '0.625rem',
  fontWeight: 528,
  verticalAlign: 'middle',
};

export function ReReviewBadge() {
  return (
    <span
      style={{
        ...BADGE_STYLE_BASE,
        background: 'var(--loop-neutral-200)',
        color: 'var(--loop-neutral-700)',
      }}
      title="This council run re-reviewed an existing PR rather than reviewing it for the first time."
    >
      RE-REVIEW
    </span>
  );
}

export function AutoBadge() {
  return (
    <span
      style={{
        ...BADGE_STYLE_BASE,
        background: 'var(--loop-blue-100)',
        color: 'var(--loop-blue-700)',
      }}
      title="This council run was auto-triggered (push-on-PR), not user-initiated via slash command."
    >
      AUTO
    </span>
  );
}

// Compact icon breakdown for verifier_results. Each count+icon span owns
// its own title attribute so a reader hovering over a single icon (rather
// than the whole chip) gets the legend for THAT icon. Previously the chip
// had one title with the full legend, which made decoding a single ◐ or ↩
// require reading the whole sentence. See #140.
//
// The first four are always shown, at 0 as well, because a run that classified
// none of a status genuinely measured that. `refuted` is the exception and is
// shown only when non-zero: it is a NEW status, so 0 on a historical run means
// "this orchestrator could not emit it" rather than "none were refuted", and
// the two must not render alike (engineering-plugins#291).
export function VerifierBreakdown({ counts }: { counts: VerifierResultsCounts }) {
  return (
    <span
      data-testid="verifier-breakdown"
      style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--loop-neutral-900)' }}
    >
      <span style={{ color: 'var(--loop-green-700)' }} title="resolved">{counts.resolved} ✅</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-yellow-800)' }} title="unresolved">{counts.unresolved} ⚠️</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-neutral-700)' }} title="partial">{counts.partial} ◐</span>
      <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
      <span style={{ color: 'var(--loop-neutral-700)' }} title="withdrawn">{counts.withdrawn} ↩</span>
      {counts.refuted > 0 && (
        <>
          <span style={{ color: 'var(--loop-neutral-500)' }}> / </span>
          <span
            style={{ color: 'var(--loop-red-700)' }}
            title="refuted — the finding was wrong when it was raised"
          >
            {counts.refuted} ✗
          </span>
        </>
      )}
    </span>
  );
}
